import { Injectable } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { ArrivalNotice } from './arrivalNotice.model';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { StatusEnum } from 'src/app/shared/models';
import { catchError, finalize, map } from 'rxjs/operators';
import { SnackBarService } from 'src/app/shared/services/snack-service.service';

@Injectable({
  providedIn: 'root'
})
export class ArrivalService {
  private _arrivalsChangedSubject = new Subject<void>();
  public arrivalsChanged$ = this._arrivalsChangedSubject.asObservable();
  constructor( private http: HttpClient,private snackService: SnackBarService,) { }

   //Arrivals

   getArrivals(projectId: number) {
    return this.http.get<ArrivalNotice[]>(
      `${environment.apiRoot}projects/${projectId}/arrivals`
    );
  }

  getArrivalsWithContent(projectId: number) {
    return this.http.get<ArrivalNotice[]>(
      `${environment.apiRoot}projects/${projectId}/arrivals?includeContent=true`
    );
  }

  getArrivalsByDateRange(projectId: number, startDate: Date, endDate: Date) {
    return this.http.get<ArrivalNotice[]>(
      `${
        environment.apiRoot
      }projects/${projectId}/arrivals?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`
    );
  }
  getArrivalsByDateRangeWithContent(projectId: number, startDate: Date, endDate: Date) {
    return this.http.get<ArrivalNotice[]>(
      `${
        environment.apiRoot
      }projects/${projectId}/arrivals?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&includeContent=true`
    );
  }
  getArrivalsByDateRangeAndStatus(
    projectId: number,
    startDate: Date,
    endDate: Date,
    status: StatusEnum
  ) {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const body = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      status: status,
    };

    return this.http
      .post<ArrivalNotice[]>(
        `${environment.apiRoot}projects/${projectId}/arrivals/byStatus`,
        body,
        { headers }
      )
      .pipe(
        map((arrivals) =>
          arrivals.map((arrival) => {
            let transformedArrival = new ArrivalNotice(arrival);
            if (Array.isArray(transformedArrival.statusEvents)) {
              transformedArrival.statusEvents.sort(
                (a, b) =>
                  new Date(a.timeStamp).getTime() -
                  new Date(b.timeStamp).getTime()
              );
            }
            return transformedArrival;
          })
        ),
        catchError((error) => {
          this.snackService.openErrorSnackBar(
            'Tapahtui virhe haettaessa varauksia',
            'OK',
            5000
          );
          return of([]); // Return a default empty object
        })
      );
  }
  getArrival(projectId: number, id: number): Observable<ArrivalNotice> {

    return this.http
      .get<ArrivalNotice>(
        `${environment.apiRoot}projects/${projectId}/arrivals/${id}`
      )
      .pipe(
        map((arrival) => {
          var model = new ArrivalNotice(arrival);
          if (Array.isArray(arrival.statusEvents)) {
            model.statusEvents.sort(
              (a, b) =>
                new Date(a.timeStamp).getTime() -
                new Date(b.timeStamp).getTime()
            );
          }
          return model;
        }),
        catchError((error:HttpErrorResponse) => {
          this.snackService.openErrorSnackBar(
            `Tapahtui virhe haettaessa varausta: ${error.message}`,
            'OK',
            5000
          );
          return of(new ArrivalNotice({})); // Return a default empty object
        })
      );
  }
  addArrival(projectId: number, arrivalFormData: FormData) {
    return this.http
      .post(
        `${environment.apiRoot}projects/${projectId}/arrivals`,
        arrivalFormData
      )
      .pipe(finalize(() => this._arrivalsChangedSubject.next()));
  }
  updateArrival(projectId: number,arrivalId:number, arrival: FormData) {
    return this.http
      .put(
        `${environment.apiRoot}projects/${projectId}/arrivals/${arrivalId}`,
        arrival
      )
      .pipe(finalize(() => this._arrivalsChangedSubject.next()));
  }

  deleteArrival(projectId: number, id: number) {
    return this.http
      .delete(`${environment.apiRoot}projects/${projectId}/arrivals/${id}`)
      .pipe(finalize(() => this._arrivalsChangedSubject.next()));
  }
  acceptArrival(projectId: number, id: number) {
    return this.http
      // State-changing: POST (the GET twin stays on the backend for one release, then goes).
      .post<ArrivalNotice>(
        `${environment.apiRoot}projects/${projectId}/arrivals/${id}/accept`,
        {}
      )
      .pipe(
        map((notice) => {
          notice.statusEvents = notice.statusEvents.sort((a, b) =>
            a.timeStamp > b.timeStamp ? -1 : 1
          );
          return notice;
        }),
        finalize(() => this._arrivalsChangedSubject.next())
      );
  }
  denyArrival(projectId: number, id: number) {
    return this.http
      // State-changing: POST (the GET twin stays on the backend for one release, then goes).
      .post<ArrivalNotice>(
        `${environment.apiRoot}projects/${projectId}/arrivals/${id}/deny`,
        {}
      )
      .pipe(
        map((notice) => {
          notice.statusEvents = notice.statusEvents.sort((a, b) =>
            a.timeStamp > b.timeStamp ? -1 : 1
          );
          return notice;
        }),
        finalize(() => this._arrivalsChangedSubject.next())
      );
  }
  setArrivalStatus(projectId: number, id: number, status: StatusEnum) {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http
      .put<ArrivalNotice>(
        `${environment.apiRoot}projects/${projectId}/arrivals/${id}/setStatus/${status}`,
        status, // Send status as an integer
        { headers }
      )
      .pipe(
        map((notice) => {
          notice.statusEvents = notice.statusEvents.sort((a, b) =>
            a.timeStamp > b.timeStamp ? -1 : 1
          );
          return notice;
        }),
        finalize(() => this._arrivalsChangedSubject.next())
      );
  }
  getWaitingNotices(projectId: number): Observable<ArrivalNotice[]> {
    return this.http.get<ArrivalNotice[]>(
      `${environment.apiRoot}projects/${projectId}/arrivals/waitingNotices`
    );
  }
  getWaitingNoticesCount(projectId: number): Observable<number> {
    return this.http.get<number>(
      `${environment.apiRoot}projects/${projectId}/arrivals/waitingNoticesCount`
    );
  }

  downloadAttachment(projectId: number, attachmentId: number): Observable<Blob> {
    return this.http.get(
      `${environment.apiRoot}projects/${projectId}/arrivals/attachments/${attachmentId}/download`,
      { responseType: 'blob' }
    );
  }
}
